<script lang="ts">
    import type {Criterion} from '../../../search/criteria.types';
    import {getFirstBy} from '../../../shared/utils/array-utils.fp';
    import {identifiedValuesEquals, isDefined} from '../../../shared/utils/custom-utils';
    import CriterionsFormManager from '../../../search/criterions-form-manager';
    import CriterionMarshaller from '../../../search/criterion-marshallers/criterion-marshaller';
    import CriterionFactory from '../../../search/criterion-factories/criterion.factory';
    import type {LocalizeFunction} from '../../../svelte/types';
    import {getContext} from 'svelte';
    import {LOCALIZE_KEY} from '../../../svelte/utils';
    import KpSearchFacet from './KpSearchFacet.svelte';
    import type {Conjunction} from '../../../search/criteria.types';
    import {isEqual} from 'lodash';
    import type {Facet} from '../../../typings/portaro.be.types';
    import {createEventDispatcher} from 'svelte';

    export let availableFacets: Facet[];
    export let selectedFacetConjunction: Conjunction;
    export let defaultExpandedFacets: string[];

    const criterionsFormManager = new CriterionsFormManager(new CriterionFactory());
    const criterionMarshaller = new CriterionMarshaller(new CriterionFactory());

    const localize: LocalizeFunction = getContext(LOCALIZE_KEY);
    const dispatch = createEventDispatcher<{'restrictions-change': Conjunction}>();

    let criterions: Criterion[] = [];
    let isAnySelected: boolean;

    $: refresh(availableFacets, selectedFacetConjunction);

    function changeRestrictions() {
        dispatch('restrictions-change', criterionMarshaller.fromCriterionsToConjunction(criterions));
    }

    function deactivateAllKeys(): void {
        criterionsFormManager.clearCriterionValues();
        criterions = criterionsFormManager.getCriterions();
        isAnySelected = criterionsFormManager.isAnyCriterionFilled();
        changeRestrictions();
    }

    function isFacetInitiallyExpanded(criterion: Criterion): boolean {
        if (!criterion.isEmpty()) {
            return true;
        }
        return isDefined(defaultExpandedFacets.find((expandedFacetId) => expandedFacetId === criterion.field.id));
    }

    function refresh(updatedFacets: Facet[], updatedFacetRestriction: Conjunction): void {
        const filledCriterions = criterionMarshaller.fromConjunctionToCriterions(updatedFacetRestriction);

        criterionsFormManager.setCriterions(
            updatedFacets
                .map((facet) => {
                    // zkusime kriterium najit ve vyplnenych, pokud nenalezneme, vytvorime nove z fieldu
                    const filledCrit = getFirstBy<Criterion>((filledCriterion) => identifiedValuesEquals(filledCriterion.field, facet))(filledCriterions);
                    if (filledCrit) {
                        filledCrit.field = facet;
                        return filledCrit;
                    }

                    return criterionsFormManager.createCriterionByField(facet);
                })
        );

        if (!isEqual(criterions, criterionsFormManager.getCriterions())) {
            criterions = criterionsFormManager.getCriterions();
        }

        isAnySelected = criterionsFormManager.isAnyCriterionFilled();
    }

</script>

<template>
    {#if availableFacets}
        <div class="panel-heading facets-panel-heading">
            <h2 class="unset-style">
                {localize(/* @kp-localization hledani.vybratZVyhledanych */ 'hledani.vybratZVyhledanych')}
            </h2>
            {#if isAnySelected}
                <button class="btn btn-default btn-xs"
                        type="button"
                        on:click={deactivateAllKeys}>
                    {localize(/* @kp-localization hledani.zrusitVsechnaOznaceni */ 'hledani.zrusitVsechnaOznaceni')}
                </button>
            {/if}
        </div>
    {/if}

    {#if availableFacets}
        <div class="facets-panel-body">
            {#each criterions as criterion (criterion.field.id)}
                <KpSearchFacet {criterion} initiallyExpanded={isFacetInitiallyExpanded(criterion)} on:criterion-change={changeRestrictions}/>
            {/each}
        </div>
    {/if}

    {#if (availableFacets && isAnySelected)}
        <div class="panel-footer facets-panel-footer">
            <button class="btn btn-default btn-xs"
                    type="button"
                    on:click={deactivateAllKeys}>
                {localize(/* @kp-localization hledani.zrusitVsechnaOznaceni */ 'hledani.zrusitVsechnaOznaceni')}
            </button>
        </div>
    {/if}
</template>
<style lang="less">
    .facets-panel-heading {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
    }
    .facets-panel-footer {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: flex-end;
    }
</style>